import { useState } from "react";
import { AppLayout } from "@project/components/AppLayout";
import { useAuth } from "@project/contexts/AuthContext";
import {
  mockJobs,
  mockCandidates,
  mockInterviews,
  pipelineStages,
  type CandidateStage,
} from "@project/data/mockData";
import {
  Briefcase,
  FileText,
  Users,
  TrendingUp,
  ArrowUpRight,
  ArrowDownRight,
  UserCheck,
  Target,
  Calendar,
  Clock,
  BarChart3,
  PieChart,
  Filter,
  MoreHorizontal,
  Eye,
  Building2,
  GripVertical,
  Sparkles,
  Loader2,
  Brain,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
} from "lucide-react";
import { useAIAction } from "@project/hooks/useAIAction";
import { Button } from "@project/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@project/components/ui/dialog";
import AIResultDisplay from "@project/components/AIResultDisplay";
import { motion } from "framer-motion";
import {
  staggerContainer as container,
  fadeInUp as item,
} from "@project/lib/animations";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPie,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ComposedChart,
} from "recharts";
import { useNavigate } from "react-router-dom";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@project/components/ui/tabs";

// ─── Data ────────────────────────────────────────────────────────────────────

const kpiStats = [
  {
    label: "Total Jobs",
    value: mockJobs.length.toString(),
    change: "+12%",
    up: true,
    icon: Briefcase,
    accent: "hsl(200 80% 35%)",
    bg: "hsl(200 80% 35% / 0.08)",
  },
  {
    label: "Active Jobs",
    value: mockJobs.filter(j => j.status === "Active").length.toString(),
    change: "+8%",
    up: true,
    icon: TrendingUp,
    accent: "hsl(160 84% 39%)",
    bg: "hsl(160 84% 39% / 0.08)",
  },
  {
    label: "Candidates",
    value: mockCandidates.length.toString(),
    change: "+23%",
    up: true,
    icon: FileText,
    accent: "hsl(38 92% 50%)",
    bg: "hsl(38 92% 50% / 0.08)",
  },
  {
    label: "Shortlisted",
    value: mockCandidates
      .filter(c => c.stage === "Shortlisted")
      .length.toString(),
    change: "+5%",
    up: true,
    icon: Users,
    accent: "hsl(199 89% 48%)",
    bg: "hsl(199 89% 48% / 0.08)",
  },
  {
    label: "Interviews",
    value: mockInterviews.length.toString(),
    change: "-3%",
    up: false,
    icon: UserCheck,
    accent: "hsl(350 80% 55%)",
    bg: "hsl(350 80% 55% / 0.08)",
  },
  {
    label: "Offers",
    value: mockCandidates
      .filter(c => c.stage === "Offered" || c.stage === "Hired")
      .length.toString(),
    change: "+18%",
    up: true,
    icon: Target,
    accent: "hsl(170 60% 42%)",
    bg: "hsl(170 60% 42% / 0.08)",
  },
];

const applicationsTrend = [
  { month: "Oct", applications: 280, shortlisted: 65, hired: 20 },
  { month: "Nov", applications: 390, shortlisted: 92, hired: 28 },
  { month: "Dec", applications: 350, shortlisted: 85, hired: 25 },
  { month: "Jan", applications: 420, shortlisted: 98, hired: 32 },
  { month: "Feb", applications: 480, shortlisted: 112, hired: 38 },
  { month: "Mar", applications: 520, shortlisted: 125, hired: 42 },
];

const departmentHiring = [
  { dept: "Eng", open: 18, filled: 24 },
  { dept: "Design", open: 8, filled: 12 },
  { dept: "Mktg", open: 5, filled: 8 },
  { dept: "Sales", open: 12, filled: 15 },
  { dept: "Finance", open: 3, filled: 6 },
  { dept: "HR", open: 2, filled: 4 },
];

const hiringFunnel = [
  {
    stage: "Applied",
    count: mockCandidates.filter(c => c.stage === "Applied").length * 50,
    pct: 100,
  },
  {
    stage: "Screened",
    count: mockCandidates.filter(c => c.stage !== "Applied").length * 30,
    pct: 67,
  },
  {
    stage: "Shortlisted",
    count: mockCandidates.filter(c => c.stage === "Shortlisted").length * 15,
    pct: 21,
  },
  {
    stage: "Interview",
    count: mockCandidates.filter(c => c.stage === "Interview").length * 10,
    pct: 12,
  },
  {
    stage: "Offered",
    count: mockCandidates.filter(c => c.stage === "Offered").length * 5,
    pct: 5,
  },
  {
    stage: "Hired",
    count: mockCandidates.filter(c => c.stage === "Hired").length * 3,
    pct: 3,
  },
];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (!active || !payload?.length) {
    return null;
  }
  return (
    <div className="rounded-xl border border-border/80 bg-card px-3 py-2 shadow-lg">
      <p className="mb-1 font-display text-[11px] font-semibold text-muted-foreground">
        {label}
      </p>
      {payload.map((p: any, i: number) => (
        <div key={i} className="flex items-center gap-2 text-[12px]">
          <div
            className="h-2 w-2 rounded-full"
            style={{ background: p.color }}
          />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-display font-bold text-foreground">
            {p.value.toLocaleString()}
          </span>
        </div>
      ))}
    </div>
  );
};

function SectionHeader({ icon: Icon, title }: { icon: any; title: string }) {
  return (
    <div className="flex items-center justify-between border-b border-border/50 px-4 pb-3 pt-4">
      <div className="flex items-center gap-2">
        <div className="bg-primary/8 flex h-7 w-7 items-center justify-center rounded-lg">
          <Icon className="h-3.5 w-3.5 text-primary" />
        </div>
        <h2 className="font-display text-[13px] font-semibold text-foreground">
          {title}
        </h2>
      </div>
      <button className="text-muted-foreground transition-colors hover:text-foreground">
        <MoreHorizontal className="h-4 w-4" />
      </button>
    </div>
  );
}

// ─── Kanban Pipeline ─────────────────────────────────────────────────────────

function KanbanPipeline() {
  const navigate = useNavigate();
  const stages: CandidateStage[] = [
    "Applied",
    "Screened",
    "Shortlisted",
    "Interview",
    "Offered",
    "Hired",
  ];

  const stageColors: Record<string, string> = {
    Applied: "border-t-[hsl(200,80%,35%)]",
    Screened: "border-t-[hsl(199,89%,48%)]",
    Shortlisted: "border-t-[hsl(38,92%,50%)]",
    Interview: "border-t-[hsl(170,60%,42%)]",
    Offered: "border-t-[hsl(160,84%,39%)]",
    Hired: "border-t-[hsl(130,60%,40%)]",
  };

  const scoreColor = (score: number) => {
    if (score >= 90) {
      return "text-[hsl(var(--status-active))] bg-[hsl(var(--status-active-bg))]";
    }
    if (score >= 75) {
      return "text-[hsl(var(--status-posted))] bg-[hsl(var(--status-posted-bg))]";
    }
    if (score >= 60) {
      return "text-[hsl(var(--status-onhold))] bg-[hsl(var(--status-onhold-bg))]";
    }
    return "text-destructive bg-[hsl(var(--status-filled-bg))]";
  };

  return (
    <div className="no-scrollbar flex gap-3 overflow-x-auto pb-2">
      {stages.map(stage => {
        const candidates = mockCandidates.filter(c => c.stage === stage);
        return (
          <div
            key={stage}
            className={`min-w-[180px] max-w-[260px] flex-1 rounded-xl border border-border/50 bg-muted/30 sm:min-w-[220px] ${stageColors[stage]} border-t-[3px]`}
          >
            <div className="flex items-center justify-between px-3 py-2.5">
              <h3 className="text-[12px] font-semibold text-foreground">
                {stage}
              </h3>
              <span className="rounded-md bg-muted px-1.5 py-0.5 text-[10px] font-bold text-muted-foreground">
                {candidates.length}
              </span>
            </div>
            <div className="no-scrollbar max-h-[250px] space-y-2 overflow-y-auto px-2 pb-2 sm:max-h-[300px]">
              {candidates.map(c => (
                <button
                  key={c.id}
                  onClick={() => navigate(`/candidates/${c.id}`)}
                  className="group w-full rounded-lg border border-border/40 bg-card p-2.5 text-left transition-all hover:border-border hover:shadow-sm"
                >
                  <div className="mb-1.5 flex items-center gap-2">
                    <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-primary to-primary-glow">
                      <span className="text-[9px] font-bold text-white">
                        {c.avatar}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-[11px] font-semibold text-foreground">
                        {c.name}
                      </p>
                      <p className="truncate text-[9px] text-muted-foreground">
                        {c.jobTitle}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between">
                    <span
                      className={`rounded px-1.5 py-0.5 text-[9px] font-bold ${scoreColor(c.matchScore)}`}
                    >
                      {c.matchScore}% match
                    </span>
                    <span className="text-[9px] text-muted-foreground">
                      {c.experience}
                    </span>
                  </div>
                </button>
              ))}
              {candidates.length === 0 && (
                <div className="py-4 text-center text-[11px] text-muted-foreground/60">
                  No candidates
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const hour = new Date().getHours();
  const greeting =
    hour < 12 ? "Good morning" : hour < 17 ? "Good afternoon" : "Good evening";
  const todayInterviews = mockInterviews.filter(i => i.status === "Scheduled");
  const [aiInsightsOpen, setAiInsightsOpen] = useState(false);
  const aiInsights = useAIAction();

  const handleAIInsights = async () => {
    setAiInsightsOpen(true);
    await aiInsights.run(`You are an HR Analytics AI. Analyze this recruitment data and provide actionable insights:

**Pipeline Data:**
- Total Jobs: ${mockJobs.length} (Active: ${mockJobs.filter(j => j.status === "Active").length}, Draft: ${mockJobs.filter(j => j.status === "Draft").length})
- Total Candidates: ${mockCandidates.length}
- Applied: ${mockCandidates.filter(c => c.stage === "Applied").length}
- Screened: ${mockCandidates.filter(c => c.stage === "Screened").length}
- Shortlisted: ${mockCandidates.filter(c => c.stage === "Shortlisted").length}
- Interview: ${mockCandidates.filter(c => c.stage === "Interview").length}
- Offered: ${mockCandidates.filter(c => c.stage === "Offered").length}
- Hired: ${mockCandidates.filter(c => c.stage === "Hired").length}
- Rejected: ${mockCandidates.filter(c => c.stage === "Rejected").length}
- Upcoming Interviews: ${todayInterviews.length}
- Avg Match Score: ${Math.round(mockCandidates.reduce((a, c) => a + c.matchScore, 0) / mockCandidates.length)}%

Provide:
1. **Key Insights** (3-4 bullet points about pipeline health)
2. **Bottleneck Analysis** (where candidates are getting stuck)
3. **Hiring Predictions** (estimated time-to-fill, conversion rates)
4. **AI Recommendations** (3-4 actionable suggestions to improve hiring)
5. **Risk Alerts** (any concerns in the data)

Format with headers and bullet points.`);
  };

  return (
    <AppLayout>
      <motion.div
        className="space-y-4"
        variants={container}
        initial="hidden"
        animate="show"
      >
        {/* Welcome */}
        <motion.div variants={item} className="section-card p-4 sm:p-5">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-display text-lg font-bold tracking-tight text-foreground sm:text-xl">
                {greeting}, {user?.name?.split(" ")[0] || "Admin"} 👋
              </h1>
              <p className="mt-1 text-[12px] text-muted-foreground sm:text-[13px]">
                <span className="font-semibold text-[hsl(var(--status-active))]">
                  {mockCandidates.filter(c => c.stage === "Applied").length} new
                  applications
                </span>{" "}
                · {todayInterviews.length} interviews today · Pipeline is 23%
                ahead
              </p>
            </div>
            <div className="hidden items-center gap-2 sm:flex">
              <Button
                size="sm"
                className="ai-btn h-8 gap-1.5 rounded-lg text-[12px] font-semibold"
                onClick={handleAIInsights}
              >
                <Brain className="h-3.5 w-3.5" />
                AI Insights
              </Button>
              <div className="flex items-center gap-1.5 rounded-lg bg-muted/50 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                <Calendar className="h-3 w-3" />
                {new Date().toLocaleDateString("en-US", {
                  month: "short",
                  day: "numeric",
                })}
              </div>
              <div className="flex items-center gap-1.5 rounded-lg bg-muted/50 px-2.5 py-1.5 text-[11px] text-muted-foreground">
                <Clock className="h-3 w-3" />
                Avg. 24 days to hire
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className="h-8 gap-1.5 rounded-lg border-primary/30 text-[11px] text-primary sm:hidden"
              onClick={handleAIInsights}
            >
              <Brain className="h-3.5 w-3.5" />
              AI
            </Button>
          </div>
        </motion.div>

        {/* KPI Cards */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-6">
          {kpiStats.map(stat => (
            <motion.div
              key={stat.label}
              variants={item}
              className="section-card p-3 sm:p-4"
            >
              <div className="mb-2 flex items-center justify-between">
                <div
                  className="flex h-8 w-8 items-center justify-center rounded-lg"
                  style={{ background: stat.bg }}
                >
                  <stat.icon
                    className="h-3.5 w-3.5"
                    style={{ color: stat.accent }}
                  />
                </div>
                <span
                  className={`inline-flex items-center gap-0.5 rounded-md px-1.5 py-0.5 text-[10px] font-bold ${stat.up ? "bg-[hsl(var(--status-active-bg))] text-[hsl(var(--status-active))]" : "bg-[hsl(var(--status-filled-bg))] text-destructive"}`}
                >
                  {stat.up ? (
                    <ArrowUpRight className="h-2.5 w-2.5" />
                  ) : (
                    <ArrowDownRight className="h-2.5 w-2.5" />
                  )}
                  {stat.change}
                </span>
              </div>
              <p className="font-display text-xl font-bold tracking-tight text-foreground sm:text-2xl">
                {stat.value}
              </p>
              <p className="mt-0.5 text-[10px] text-muted-foreground sm:text-[11px]">
                {stat.label}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Pipeline Kanban */}
        <motion.div variants={item} className="section-card overflow-hidden">
          <SectionHeader icon={GripVertical} title="Candidate Pipeline" />
          <div className="p-3 sm:p-4">
            <KanbanPipeline />
          </div>
        </motion.div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
          <motion.div
            variants={item}
            className="section-card overflow-hidden lg:col-span-2"
          >
            <SectionHeader icon={BarChart3} title="Applications Overview" />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={240}>
                <ComposedChart data={applicationsTrend}>
                  <defs>
                    <linearGradient id="appGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="hsl(200 80% 35%)"
                        stopOpacity={0.25}
                      />
                      <stop
                        offset="100%"
                        stopColor="hsl(200 80% 35%)"
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(220 13% 91%)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="month"
                    tick={{ fontSize: 10, fill: "hsl(0 0% 40%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "hsl(0 0% 40%)" }}
                    axisLine={false}
                    tickLine={false}
                    width={30}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Area
                    type="monotone"
                    dataKey="applications"
                    name="Applications"
                    fill="url(#appGrad)"
                    stroke="hsl(200 80% 35%)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="shortlisted"
                    name="Shortlisted"
                    stroke="hsl(199 89% 48%)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="hired"
                    name="Hired"
                    stroke="hsl(160 84% 39%)"
                    strokeWidth={2}
                    dot={{ r: 2.5, fill: "hsl(160 84% 39%)" }}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div variants={item} className="section-card overflow-hidden">
            <SectionHeader icon={Filter} title="Hiring Funnel" />
            <div className="space-y-2 p-3 sm:p-4">
              {hiringFunnel.map((stage, i) => {
                const colors = [
                  "hsl(200 80% 35%)",
                  "hsl(199 89% 48%)",
                  "hsl(38 92% 50%)",
                  "hsl(170 60% 42%)",
                  "hsl(160 84% 39%)",
                  "hsl(130 60% 40%)",
                ];
                return (
                  <div key={stage.stage}>
                    <div className="mb-0.5 flex items-center justify-between">
                      <span className="text-[11px] font-medium text-foreground">
                        {stage.stage}
                      </span>
                      <div className="flex items-center gap-1.5">
                        <span className="font-display text-[12px] font-bold text-foreground">
                          {stage.count}
                        </span>
                        <span className="rounded bg-muted px-1 py-0.5 text-[9px] text-muted-foreground">
                          {stage.pct}%
                        </span>
                      </div>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-muted">
                      <motion.div
                        className="h-full rounded-full"
                        style={{ background: colors[i] }}
                        initial={{ width: 0 }}
                        animate={{ width: `${stage.pct}%` }}
                        transition={{
                          delay: 0.3 + i * 0.08,
                          duration: 0.6,
                          ease: "easeOut",
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </motion.div>
        </div>

        {/* Row 2: Dept + Upcoming Interviews */}
        <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
          <motion.div variants={item} className="section-card overflow-hidden">
            <SectionHeader icon={Building2} title="Department Hiring" />
            <div className="p-3 sm:p-4">
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={departmentHiring} barGap={2} barSize={14}>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(220 13% 91%)"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="dept"
                    tick={{ fontSize: 10, fill: "hsl(0 0% 40%)" }}
                    axisLine={false}
                    tickLine={false}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "hsl(0 0% 40%)" }}
                    axisLine={false}
                    tickLine={false}
                    width={25}
                  />
                  <Tooltip content={<CustomTooltip />} />
                  <Bar
                    dataKey="open"
                    name="Open"
                    fill="hsl(200 80% 35%)"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="filled"
                    name="Filled"
                    fill="hsl(160 84% 39%)"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </motion.div>

          <motion.div variants={item} className="section-card overflow-hidden">
            <SectionHeader icon={Calendar} title="Upcoming Interviews" />
            <div className="space-y-2.5 p-3 sm:p-4">
              {todayInterviews.slice(0, 5).map(interview => (
                <button
                  key={interview.id}
                  onClick={() => navigate(`/interviews/${interview.id}`)}
                  className="flex w-full items-center gap-3 rounded-xl p-2.5 text-left transition-all hover:bg-accent/40"
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br from-primary to-primary-glow">
                    <span className="text-[10px] font-bold text-white">
                      {interview.candidateAvatar}
                    </span>
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[12px] font-semibold text-foreground">
                      {interview.candidateName}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {interview.jobTitle} · {interview.round}
                    </p>
                  </div>
                  <div className="shrink-0 text-right">
                    <p className="text-[11px] font-semibold text-foreground">
                      {interview.time}
                    </p>
                    <p className="text-[10px] text-muted-foreground">
                      {interview.date}
                    </p>
                  </div>
                </button>
              ))}
              {todayInterviews.length === 0 && (
                <div className="py-6 text-center text-[12px] text-muted-foreground">
                  No upcoming interviews
                </div>
              )}
            </div>
          </motion.div>
        </div>
      </motion.div>

      {/* AI Insights Dialog */}
      <Dialog open={aiInsightsOpen} onOpenChange={setAiInsightsOpen}>
        <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display">
              <Brain className="h-5 w-5 text-primary" />
              AI Hiring Insights & Predictions
            </DialogTitle>
          </DialogHeader>
          {aiInsights.loading ? (
            <div className="flex flex-col items-center gap-3 py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <p className="text-[13px] text-muted-foreground">
                Analyzing pipeline data...
              </p>
            </div>
          ) : (
            <AIResultDisplay content={aiInsights.result} />
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
};

export default Dashboard;
